<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import { ArrowLeft, ChevronDown } from 'lucide-vue-next'
  import { Button } from '@/components/ui/button'
  import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
  import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
  } from '@/components/ui/table'
  import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger
  } from '@/components/ui/alert-dialog'
  import SpaCompatComponent from '@/components/app/SpaCompatComponent.vue'
  import SortableTableHeader from '@/components/admin/SortableTableHeader.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { DuplicateCaseMatchesService, CasesService } from '@/types/generated/services.gen'
  import type { DuplicateCaseMatch, CustomerCase } from '@/types/generated/types.gen'
  import { EntityTypeEnum } from '@/types/generated/types.gen'
  import { useToast } from '@/components/ui/toast/use-toast'
  import ChatEmptyStateIllustration from '@/components/svg/Illustrations/ChatEmptyStateIllustration.vue'

  const router = useRouter()
  const route = useRoute()
  const { toast } = useToast()

  const { t } = useTranslation({
    en: {
      back: 'Back to Case Overview',
      newCaseDescription:
        'This is the newly created case. It will be merged into an existing case.',
      selectCaseToRemain: 'Select the case that should remain after the merge.',
      clientName: 'Client Name',
      opponentName: 'Opponent Name',
      received: 'Received',
      lawFieldTopic: 'Law field / Topic',
      note: 'Note',
      cancel: 'Cancel',
      mergeDuplicateCases: 'Merge Duplicate Cases',
      mergeAndUpdate: 'Merge & Update',
      mergeCases: 'Merge cases',
      mergeDialogTitle: 'Merge requests and remove duplicates',
      mergeDialogDescription:
        'After merging, duplicate requests will be permanently deleted and cannot be restored. All selected information will be retained.',
      mergeDialogNote:
        'After merging, go to the existing requests to confirm the update of protected fields such as phone numbers and email addresses.',
      caseSummary: 'Case summary',
      clientInformation: 'Client Information',
      opponentInformation: 'Opponent Information',
      salutation: 'Salutation:',
      firstName: 'First name:',
      lastName: 'Last name:',
      dateOfBirth: 'Date of birth:',
      phone: 'Phone:',
      email: 'Email:',
      street: 'Street:',
      streetNr: 'Street No.:',
      postalCode: 'Postal code:',
      city: 'City:',
      companyName: 'Company name:',
      legalForm: 'Legal form:',
      institutionName: 'Institution name:',
      contactFirstName: 'Contact first name:',
      contactLastName: 'Contact last name:',
      contactDateOfBirth: 'Contact date of birth:',
      ai: 'AI',
      loadingDuplicateCases: 'Loading duplicate cases...',
      noDuplicateCasesFound: 'No duplicate cases found.',
      backToCase: 'Back to Case',
      retry: 'Retry',
      error: 'Error',
      failedToLoadDuplicateCases: 'Failed to load duplicate cases.'
    },
    de: {
      back: 'Zurück zur Fallübersicht',
      newCaseDescription:
        'Dies ist der neu erstellte Fall. Er wird in einen bestehenden Fall zusammengeführt.',
      selectCaseToRemain:
        'Wählen Sie den Fall aus, der nach der Zusammenführung bestehen bleiben soll.',
      clientName: 'Mandantenname',
      opponentName: 'Gegnername',
      received: 'Eingegangen',
      lawFieldTopic: 'Rechtsgebiet / Thema',
      note: 'Notiz',
      cancel: 'Abbrechen',
      mergeDuplicateCases: 'Duplikat-Anfragen zusammenführen',
      mergeAndUpdate: 'Zusammenführen & Aktualisieren',
      mergeCases: 'Anfragen zusammenführen',
      mergeDialogTitle: 'Anfragen zusammenführen und Duplikate entfernen',
      mergeDialogDescription:
        'Nach dem Zusammenführen werden doppelte Anfragen dauerhaft gelöscht und können nicht wiederhergestellt werden. Alle ausgewählten Informationen bleiben erhalten.',
      mergeDialogNote:
        'Gehen Sie nach dem Zusammenführen zu den bestehenden Anfragen, um die Aktualisierung geschützter Felder wie Telefonnummern und E-Mail-Adressen zu bestätigen.',
      caseSummary: 'Fallzusammenfassung',
      clientInformation: 'Mandanteninformationen',
      opponentInformation: 'Gegnerinformationen',
      salutation: 'Anrede:',
      firstName: 'Vorname:',
      lastName: 'Nachname:',
      dateOfBirth: 'Geburtsdatum:',
      phone: 'Telefon:',
      email: 'E-Mail:',
      street: 'Straße:',
      streetNr: 'Hausnummer:',
      postalCode: 'Postleitzahl:',
      city: 'Stadt:',
      companyName: 'Firmenname:',
      legalForm: 'Rechtsform:',
      institutionName: 'Institutionsname:',
      contactFirstName: 'Kontakt Vorname:',
      contactLastName: 'Kontakt Nachname:',
      contactDateOfBirth: 'Kontakt Geburtsdatum:',
      ai: 'KI',
      loadingDuplicateCases: 'Duplikat-Anfragen werden geladen...',
      noDuplicateCasesFound: 'Keine Duplikat-Anfragen gefunden.',
      backToCase: 'Zurück zum Fall',
      retry: 'Erneut versuchen',
      error: 'Fehler',
      failedToLoadDuplicateCases: 'Fehler beim Laden der Duplikat-Anfragen.'
    }
  })

  const selectedCaseUuid = ref<string>('')
  const expandedRowUuid = ref<string | null>(null)
  const sortBy = ref<string>('-received_date')

  const sourceCaseUuid = ref<string>('')
  const isButtonDisabled = computed(() => !selectedCaseUuid.value)
  const isLoading = ref(true)
  const fetchError = ref(false)

  const sourceCase = ref<CustomerCase | null>(null)
  const duplicateCases = ref<DuplicateCaseMatch[]>([])

  // Computed properties for source case entities
  const sourceCaseClient = computed(() => {
    return sourceCase.value?.data?.client
  })

  const sourceCaseOpponent = computed(() => {
    return sourceCase.value?.data?.opponent
  })

  // Helper functions to format data for display
  type Entity = DuplicateCaseMatch['target_case_client']

  const formatEntityName = (entity: Entity | undefined) => {
    if (!entity) return 'N/A'

    const firstName = entity.first_name?.trim()
    const lastName = entity.last_name?.trim()

    const getPersonalName = () => {
      return [firstName, lastName].filter(Boolean).join(' ') || null
    }

    switch (entity.entity_type) {
      case EntityTypeEnum.NATURAL_PERSON: {
        return getPersonalName() || 'N/A'
      }

      case EntityTypeEnum.LEGAL_PERSON: {
        const companyName = entity.company_name?.trim()
        return companyName || getPersonalName() || 'N/A'
      }

      case EntityTypeEnum.INSTITUTION: {
        const institutionName = entity.institution_name?.trim()
        return institutionName || getPersonalName() || 'N/A'
      }

      default:
        return 'N/A'
    }
  }

  const formatDate = (dateStr: string | undefined | null, fallback: string = 'N/A') => {
    if (!dateStr) return fallback
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  const formatLawFieldTopic = (field: string | undefined, topic: string | undefined) => {
    if (!field && !topic) return ''
    if (!field) return topic || ''
    if (!topic) return field
    return `${field} / ${topic}`
  }

  const sortedCases = computed(() => {
    const cases = [...duplicateCases.value]
    const field = sortBy.value.replace('-', '')
    const isDesc = sortBy.value.startsWith('-')

    if (field === 'received_date') {
      cases.sort((a, b) => {
        const aTime = new Date(a.target_case_created_at).getTime()
        const bTime = new Date(b.target_case_created_at).getTime()

        if (aTime < bTime) return isDesc ? 1 : -1
        if (aTime > bTime) return isDesc ? -1 : 1
        return 0
      })
    }

    return cases
  })

  const handleSort = (field: string, direction: 'asc' | 'desc') => {
    sortBy.value = direction === 'desc' ? `-${field}` : field
  }

  const handleMerge = () => {
    if (!selectedCaseUuid.value) return
    console.log('Merging case into:', selectedCaseUuid.value)
    // TODO: Implement actual merge logic when API is ready
  }

  const handleBack = () => {
    router.push(`/app/cases/legacy/${sourceCaseUuid.value}`)
  }

  const handleCancel = () => {
    // ToDo: Reject the duplicate case and mark as rejected
    handleBack()
  }

  const isExpanded = (caseUuid: string) => {
    return expandedRowUuid.value === caseUuid
  }

  const toggleExpanded = (caseUuid: string) => {
    if (isExpanded(caseUuid)) {
      expandedRowUuid.value = null
    } else {
      expandedRowUuid.value = caseUuid
    }
  }

  const getInfoFieldsRows = (entity: Entity | undefined) => {
    if (!entity) return []

    // For legal persons (companies)
    if (entity.entity_type === EntityTypeEnum.LEGAL_PERSON) {
      return [
        [
          { label: t('companyName'), value: entity.company_name || 'N/A' },
          { label: t('legalForm'), value: entity.company_legal_form || 'N/A' }
        ],
        [
          { label: t('contactFirstName'), value: entity.first_name || 'N/A' },
          { label: t('contactLastName'), value: entity.last_name || 'N/A' }
        ],
        [
          { label: t('contactDateOfBirth'), value: formatDate(entity.date_of_birth) },
          { label: t('phone'), value: entity.phone || 'N/A' }
        ],
        [{ label: t('email'), value: entity.email || 'N/A' }]
      ]
    }

    // For institutions
    if (entity.entity_type === EntityTypeEnum.INSTITUTION) {
      return [
        [{ label: t('institutionName'), value: entity.institution_name || 'N/A' }],
        [
          { label: t('contactFirstName'), value: entity.first_name || 'N/A' },
          { label: t('contactLastName'), value: entity.last_name || 'N/A' }
        ],
        [
          { label: t('contactDateOfBirth'), value: formatDate(entity.date_of_birth) },
          { label: t('phone'), value: entity.phone || 'N/A' }
        ],
        [{ label: t('email'), value: entity.email || 'N/A' }]
      ]
    }

    // For natural persons (default)
    return [
      [{ label: t('salutation'), value: 'N/A' }],
      [
        { label: t('firstName'), value: entity.first_name || 'N/A' },
        { label: t('lastName'), value: entity.last_name || 'N/A' }
      ],
      [
        { label: t('dateOfBirth'), value: formatDate(entity.date_of_birth) },
        { label: t('phone'), value: entity.phone || 'N/A' }
      ],
      [{ label: t('email'), value: entity.email || 'N/A' }]
    ]
  }

  // Fetch source case and duplicate case matches from API
  const fetchDuplicateCases = async () => {
    try {
      isLoading.value = true
      fetchError.value = false
      // Fetch source case and duplicate matches in parallel
      const [source, matchesResponse] = (await Promise.all([
        CasesService.casesRetrieve({ uuid: sourceCaseUuid.value }),
        DuplicateCaseMatchesService.duplicateCaseMatchesForCaseList({
          caseUuid: sourceCaseUuid.value
        })
      ])) as [CustomerCase, DuplicateCaseMatch[]]

      sourceCase.value = source
      duplicateCases.value = matchesResponse
    } catch (err: any) {
      console.error('Error fetching duplicate cases:', err)
      fetchError.value = true
      duplicateCases.value = []
      toast({
        title: t('error'),
        description: t('failedToLoadDuplicateCases'),
        variant: 'destructive'
      })
    } finally {
      isLoading.value = false
    }
  }

  onMounted(async () => {
    sourceCaseUuid.value = route.params.uuid as string
    await fetchDuplicateCases()
  })
</script>

<template>
  <SpaCompatComponent>
    <div class="min-h-screen bg-gray-50">
      <div class="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <button
          class="mb-4 inline-flex items-center gap-2 text-sm text-neutral-500 underline hover:text-neutral-900"
          @click="handleBack"
        >
          <ArrowLeft class="h-5 w-5" />
          {{ t('back') }}
        </button>
        <h1 class="mb-6 text-xl font-semibold text-neutral-900">{{ t('mergeDuplicateCases') }}</h1>

        <!-- Loading State -->
        <div
          v-if="isLoading"
          class="overflow-hidden rounded-lg border border-neutral-200 bg-white p-8 text-center shadow-sm"
        >
          <p class="text-neutral-600">{{ t('loadingDuplicateCases') }}</p>
        </div>

        <!-- Error State -->
        <div
          v-else-if="fetchError"
          class="overflow-hidden rounded-lg border border-neutral-200 bg-white p-8 text-center shadow-sm"
        >
          <p class="text-neutral-600">{{ t('failedToLoadDuplicateCases') }}</p>
          <Button
            class="mt-4"
            @click="fetchDuplicateCases"
          >
            {{ t('retry') }}
          </Button>
        </div>

        <!-- No Duplicates Found -->
        <div
          v-else-if="duplicateCases.length === 0"
          class="overflow-hidden rounded-lg border border-neutral-200 bg-white p-8 text-center shadow-sm"
        >
          <p class="text-neutral-600">{{ t('noDuplicateCasesFound') }}</p>
          <ChatEmptyStateIllustration class="h-60 w-60" />
          <Button
            class="mt-4"
            @click="handleBack"
          >
            {{ t('backToCase') }}
          </Button>
        </div>

        <!-- Main Content -->
        <div
          v-else-if="sourceCase"
          class="overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-sm"
        >
          <div class="p-4">
            <!-- Source Case Section -->
            <p class="mb-4 text-base text-neutral-700">
              {{ t('newCaseDescription') }}
            </p>
            <Table>
              <TableHeader>
                <TableRow class="border-b border-neutral-200 bg-neutral-50/50">
                  <TableHead class="w-[52px]"></TableHead>
                  <TableHead class="text-sm font-semibold text-neutral-800">
                    {{ t('clientName') }}
                  </TableHead>
                  <TableHead class="text-sm font-semibold text-neutral-800">
                    {{ t('opponentName') }}
                  </TableHead>
                  <TableHead class="text-sm font-semibold text-neutral-800">
                    {{ t('received') }}
                  </TableHead>
                  <TableHead class="text-sm font-semibold text-neutral-800">
                    {{ t('lawFieldTopic') }}
                  </TableHead>
                  <TableHead class="text-sm font-semibold text-neutral-800">
                    {{ t('note') }}
                  </TableHead>
                  <TableHead class="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow
                  class="border-b border-neutral-100 hover:bg-neutral-50/50"
                  :class="{ 'bg-gray-50 hover:bg-gray-50': isExpanded(sourceCase?.uuid || '') }"
                >
                  <TableCell class="w-[40px]"></TableCell>
                  <TableCell class="py-3 text-sm font-medium text-neutral-900">
                    {{ formatEntityName(sourceCaseClient) }}
                  </TableCell>
                  <TableCell class="py-3 text-sm text-neutral-700">
                    {{ formatEntityName(sourceCaseOpponent) }}
                  </TableCell>
                  <TableCell class="whitespace-nowrap py-3 text-sm text-neutral-700">
                    {{ formatDate(sourceCase?.created_at) }}
                  </TableCell>
                  <TableCell class="py-1.5 text-sm">
                    <div class="text-neutral-800">
                      {{
                        formatLawFieldTopic(
                          sourceCase?.data?.law?.field,
                          sourceCase?.data?.law?.topic
                        )
                      }}
                    </div>
                  </TableCell>
                  <TableCell class="max-w-[200px] py-2">
                    <div class="line-clamp-2 text-sm text-neutral-800">
                      {{ sourceCase?.note || '' }}
                    </div>
                  </TableCell>
                  <TableCell class="w-[50px]">
                    <button
                      class="flex h-full w-full items-center justify-center text-neutral-500 hover:text-neutral-900"
                      @click="toggleExpanded(sourceCase?.uuid || '')"
                    >
                      <ChevronDown
                        class="h-6 w-6 transition-transform"
                        :class="{ 'rotate-180': isExpanded(sourceCase?.uuid || '') }"
                      />
                    </button>
                  </TableCell>
                </TableRow>
                <TableRow
                  v-if="isExpanded(sourceCase?.uuid || '')"
                  class="border-b border-neutral-100 bg-gray-50 hover:bg-gray-50"
                >
                  <TableCell
                    class="p-3"
                    colspan="7"
                  >
                    <div class="mb-6 flex gap-6">
                      <!-- Source Case Client Information -->
                      <div class="flex-1">
                        <h3 class="mb-2 text-sm font-medium text-neutral-900">
                          {{ t('clientInformation') }}
                        </h3>
                        <div class="rounded-lg bg-gray-100 p-4">
                          <div class="flex flex-col gap-4">
                            <div
                              v-for="(row, rowIndex) in getInfoFieldsRows(sourceCaseClient)"
                              :key="rowIndex"
                              class="grid grid-cols-2 gap-4"
                            >
                              <div
                                v-for="field in row"
                                :key="field.label"
                                class="flex gap-2 text-sm"
                              >
                                <span class="font-medium text-neutral-900">
                                  {{ field.label }}
                                </span>
                                <span class="text-neutral-700">{{ field.value }}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <!-- Source Case Opponent Information -->
                      <div class="flex-1">
                        <h3 class="mb-2 text-sm font-medium text-neutral-900">
                          {{ t('opponentInformation') }}
                        </h3>
                        <div class="rounded-lg bg-gray-100 p-4">
                          <div class="flex flex-col gap-4">
                            <div
                              v-for="(row, rowIndex) in getInfoFieldsRows(sourceCaseOpponent)"
                              :key="rowIndex"
                              class="grid grid-cols-2 gap-4"
                            >
                              <div
                                v-for="field in row"
                                :key="field.label"
                                class="flex gap-2 text-sm"
                              >
                                <span class="font-medium text-neutral-900">
                                  {{ field.label }}
                                </span>
                                <span class="text-neutral-700">{{ field.value }}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Case Summary -->
                    <div>
                      <div class="mb-2 flex items-center gap-2">
                        <h3 class="flex items-center gap-2 text-sm font-medium text-neutral-900">
                          {{ t('caseSummary') }}
                        </h3>
                        <span
                          class="flex h-5 w-5 items-center justify-center rounded-sm bg-purple-200 p-1 text-xs font-semibold
                            text-purple-700"
                        >
                          {{ t('ai') }}
                        </span>
                      </div>
                      <div class="rounded-lg bg-gray-100 p-4">
                        <p class="text-sm text-neutral-700">{{ sourceCase?.note || '' }}</p>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <!-- Duplicate Cases Section -->
            <p class="mb-4 mt-6 text-base text-neutral-700">
              {{ t('selectCaseToRemain') }}
            </p>
            <RadioGroup v-model="selectedCaseUuid">
              <Table>
                <TableHeader>
                  <TableRow class="border-b border-neutral-200 bg-neutral-50/50">
                    <TableHead class="w-[52px]"></TableHead>
                    <TableHead class="text-sm font-semibold text-neutral-800">
                      {{ t('clientName') }}
                    </TableHead>
                    <TableHead class="text-sm font-semibold text-neutral-800">
                      {{ t('opponentName') }}
                    </TableHead>
                    <SortableTableHeader
                      field="received_date"
                      :current-sort="sortBy"
                      class="text-sm font-semibold text-neutral-800"
                      @sort="handleSort"
                    >
                      {{ t('received') }}
                    </SortableTableHeader>
                    <TableHead class="text-sm font-semibold text-neutral-800">
                      {{ t('lawFieldTopic') }}
                    </TableHead>
                    <TableHead class="text-sm font-semibold text-neutral-800">
                      {{ t('note') }}
                    </TableHead>
                    <TableHead class="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <template
                    v-for="caseItem in sortedCases"
                    :key="caseItem.uuid"
                  >
                    <TableRow
                      class="border-b border-neutral-100 hover:bg-neutral-50/50"
                      :class="{
                        'bg-gray-50 hover:bg-gray-50': isExpanded(caseItem.target_case_uuid)
                      }"
                    >
                      <TableCell class="py-3">
                        <RadioGroupItem
                          :value="caseItem.target_case_uuid"
                          class="h-5 w-5"
                        />
                      </TableCell>

                      <TableCell class="py-3 text-sm font-medium text-neutral-900">
                        {{ formatEntityName(caseItem.target_case_client) }}
                      </TableCell>

                      <TableCell class="py-3 text-sm text-neutral-700">
                        {{ formatEntityName(caseItem.target_case_opponent) }}
                      </TableCell>

                      <TableCell class="whitespace-nowrap py-3 text-sm text-neutral-700">
                        {{ formatDate(caseItem.target_case_created_at) }}
                      </TableCell>

                      <TableCell class="py-1.5 text-sm">
                        <div class="text-neutral-800">
                          {{
                            formatLawFieldTopic(
                              caseItem.target_case_law_field,
                              caseItem.target_case_law_topic
                            )
                          }}
                        </div>
                      </TableCell>

                      <TableCell class="max-w-[200px] py-2">
                        <div class="line-clamp-2 text-sm text-neutral-800">
                          {{ caseItem.target_case_note }}
                        </div>
                      </TableCell>

                      <TableCell class="w-[50px]">
                        <button
                          class="flex h-full w-full items-center justify-center text-neutral-500 hover:text-neutral-900"
                          @click="toggleExpanded(caseItem.target_case_uuid)"
                        >
                          <ChevronDown
                            class="h-6 w-6 transition-transform"
                            :class="{ 'rotate-180': isExpanded(caseItem.target_case_uuid) }"
                          />
                        </button>
                      </TableCell>
                    </TableRow>

                    <!-- Expanded Row Content -->
                    <TableRow
                      v-if="isExpanded(caseItem.target_case_uuid)"
                      class="border-b border-neutral-100 bg-gray-50 hover:bg-gray-50"
                    >
                      <TableCell
                        class="p-3"
                        colspan="7"
                      >
                        <div class="mb-6 flex gap-6">
                          <!-- Target Case Client Information -->
                          <div class="flex-1">
                            <h3 class="mb-2 text-sm font-medium text-neutral-900">
                              {{ t('clientInformation') }}
                            </h3>
                            <div class="rounded-lg bg-gray-100 p-4">
                              <div class="flex flex-col gap-4">
                                <div
                                  v-for="(row, rowIndex) in getInfoFieldsRows(
                                    caseItem.target_case_client
                                  )"
                                  :key="rowIndex"
                                  class="grid grid-cols-2 gap-4"
                                >
                                  <div
                                    v-for="field in row"
                                    :key="field.label"
                                    class="flex gap-2 text-sm"
                                  >
                                    <span class="font-medium text-neutral-900">
                                      {{ field.label }}
                                    </span>
                                    <span class="text-neutral-700">{{ field.value }}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <!-- Target Case Opponent Information -->
                          <div class="flex-1">
                            <h3 class="mb-2 text-sm font-medium text-neutral-900">
                              {{ t('opponentInformation') }}
                            </h3>
                            <div class="rounded-lg bg-gray-100 p-4">
                              <div class="flex flex-col gap-4">
                                <div
                                  v-for="(row, rowIndex) in getInfoFieldsRows(
                                    caseItem.target_case_opponent
                                  )"
                                  :key="rowIndex"
                                  class="grid grid-cols-2 gap-4"
                                >
                                  <div
                                    v-for="field in row"
                                    :key="field.label"
                                    class="flex gap-2 text-sm"
                                  >
                                    <span class="font-medium text-neutral-900">
                                      {{ field.label }}
                                    </span>
                                    <span class="text-neutral-700">{{ field.value }}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Target Case Case Summary -->
                        <div>
                          <div class="mb-2 flex items-center gap-2">
                            <h3
                              class="flex items-center gap-2 text-sm font-medium text-neutral-900"
                            >
                              {{ t('caseSummary') }}
                            </h3>
                            <span
                              class="flex h-5 w-5 items-center justify-center rounded-sm bg-purple-200 p-1 text-xs font-semibold
                                text-purple-700"
                            >
                              {{ t('ai') }}
                            </span>
                          </div>
                          <div class="rounded-lg bg-gray-100 p-4">
                            <p class="text-sm text-neutral-700">{{ caseItem.reasoning }}</p>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  </template>
                </TableBody>
              </Table>
            </RadioGroup>
          </div>
        </div>

        <div
          v-if="duplicateCases.length > 0"
          class="mt-6 flex justify-end gap-3"
        >
          <Button
            variant="secondary"
            @click="handleCancel"
          >
            {{ t('cancel') }}
          </Button>

          <AlertDialog>
            <AlertDialogTrigger as-child>
              <Button :disabled="isButtonDisabled">
                {{ t('mergeAndUpdate') }}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{{ t('mergeDialogTitle') }}</AlertDialogTitle>
                <AlertDialogDescription class="text-sm font-normal text-neutral-800">
                  {{ t('mergeDialogDescription') }}
                </AlertDialogDescription>
                <ul class="list-disc pl-5 text-sm font-normal text-neutral-800">
                  <li>{{ t('mergeDialogNote') }}</li>
                </ul>
              </AlertDialogHeader>
              <AlertDialogFooter class="flex gap-4">
                <AlertDialogCancel class="flex-1">{{ t('cancel') }}</AlertDialogCancel>
                <AlertDialogAction
                  class="flex-1 bg-red-600 text-white hover:bg-red-700"
                  @click="handleMerge"
                >
                  {{ t('mergeCases') }}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  </SpaCompatComponent>
</template>
